<template>
	<!-- Las contrapartes son filas planas de nivel 1: comparten el gris medio
		de nivel 3 (son hojas, no cuentas agrupadoras de nivel 2), pero sin la
		sangría `pl-5` porque no cuelgan de un padre. -->
	<tr :class="['theme_row_white', { 'row-level-2': row.level === 2, 'row-level-3': row.level === 3 || row.level === 1 }]">
		<th scope="row"
			:class="['sticky-column nobreak theme_row_white is-capitalized', row.level === 3 ? 'pl-5' : '']"
			@click="onLabelClick"
			:style="row.level === 2 ? 'cursor: pointer;' : ''">
			<div class="is-flex is-align-items-center is-justify-content-space-between">
				<div class="is-flex is-align-items-center label_cell">
					<span v-if="row.label.length > 30"
						v-tooltip.right="row.label"
						class="label_text">
						{{ row.label.substring(0, 30) + "..." }}
					</span>
					<span v-else class="label_text">
						{{ row.label }}
					</span>
				</div>
				<ArrowDown v-if="row.level === 2" class="expand-icon"
					:class="{ 'rotate-icon': expandedL2Rows.includes(row.label) }" />
			</div>
		</th>
		<td v-if="showAccumulated"
			class="sticky-column sticky-column--acc has-text-right nobreak acc_cell theme_row_white">
			<span class="acc_amount">{{ parseToMoney(row.accumulated) }}</span>
			<span class="acc_pct" :class="pctClass(row.pct)">
				{{ formatPct(row.pct) }}
			</span>
		</td>
		<td v-for="(val, idx) in row.values" :key="idx"
			class="has-text-right nobreak theme_row_white"
			:class="{ clickable_cell: cellsClickable }"
			@click="onCellClick(monthHeaders[idx])">
			<!-- En % de ventas la celda muestra el share del gasto sobre las
				ventas de esa columna (val / ventas); si no, el monto. El tooltip
				(opt-in vía cellTooltips) carga la lectura temporal MoM/QoQ + YoY,
				anclado al número para que no quede lejos en columnas anchas. -->
			<span v-if="cellTooltips" class="cell_value"
				v-tooltip.top="cellVariationTooltip(row, idx, filterPeriod)">
				{{ percentMode ? sharePct(val, idx) : parseToMoney(val) }}
			</span>
			<template v-else>
				{{ percentMode ? sharePct(val, idx) : parseToMoney(val) }}
			</template>
		</td>
	</tr>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatVariation } from "@/utils/percentage";
import { cellVariationTooltip } from "@/views/financialReports/utils/cellTooltip";
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
	name: "EvolutionTableDataRow",

	components: { ArrowDown },

	props: {
		row: { type: Object, required: true },
		monthHeaders: { type: Array, required: true },
		expandedL2Rows: { type: Array, default: () => [] },
		showAccumulated: { type: Boolean, default: false },
		cellsClickable: { type: Boolean, default: false },
		// % de ventas: muestra cada celda mensual como share sobre ventas.
		percentMode: { type: Boolean, default: false },
		// Ventas por columna, alineadas a monthHeaders (denominador del share).
		salesValues: { type: Array, default: () => [] },
		// Opt-in: tooltip per-cell con MoM/QoQ + YoY (como Ventas). Requiere que
		// la fila traiga `prevYearValues[]` para el YoY. Granularidad vía
		// filterPeriod (decide MoM vs QoQ; null en YTD/Anual).
		cellTooltips: { type: Boolean, default: false },
		filterPeriod: { type: String, default: "month" },
	},

	methods: {
		parseToMoney,
		cellVariationTooltip,
		// Share del gasto sobre las ventas de la columna idx. Sin ventas → "—".
		// Usa el formateador canónico (mismo que el % de Ventas).
		sharePct(val, idx) {
			const ventas = this.salesValues[idx];
			if (!ventas) return "—";
			return formatVariation(val / ventas);
		},
		onLabelClick() {
			if (this.row.level !== 2) return;
			this.$emit("toggle-l2", this.row.label);
		},
		onCellClick(monthLabel) {
			if (!this.cellsClickable) return;
			this.$emit("cell-click", {
				monthLabel,
				account: this.row.label,
				level: this.row.level != null ? this.row.level : null,
				parent: this.row.parent != null ? this.row.parent : null,
			});
		},
		// pct viene como ratio (0.123 = +12,3%), null cuando no hay comparable.
		// Delegamos en el formateador canónico — withArrow + withSign reproduce
		// "▲ +12,3%" / "▼ -12,3%" / "0,0%" / "—" del formato original.
		formatPct(pct) {
			return formatVariation(pct, { withArrow: true, withSign: true });
		},
		pctClass(pct) {
			if (pct == null) return "acc_pct--neutral";
			if (pct > 0) return "acc_pct--positive";
			if (pct < 0) return "acc_pct--negative";
			return "acc_pct--neutral";
		},
	},
};
</script>

<style scoped lang="scss">
// Los estilos viven en este SFC porque el `<style scoped>` del parent
// (ExpensesEvolutionTable) no alcanza el DOM de este subcomponente. Las
// clases que comparten con las filas Total / ratio del parent están
// duplicadas allá a propósito.

th, td {
	height: 44px;
	padding: 12px 16px;
	border-bottom: 1px solid var(--report-divider-soft);
	font-size: 13px;
}

.sticky-column {
	position: sticky;
	left: 0;
	z-index: 2;
	border-right: 1px solid var(--report-border);
}

.sticky-column--acc {
	left: var(--sticky-col-1-width, 240px);
	box-sizing: border-box;
}

.acc_cell {
	.acc_amount {
		display: block;
		color: var(--report-text);
		font-size: 13px;
		font-weight: 500;
		line-height: 18px;
	}

	.acc_pct {
		display: block;
		font-size: 12px;
		font-weight: 500;
		line-height: 16px;
		margin-top: 2px;
	}

	.acc_pct--positive { color: var(--report-positive); }
	.acc_pct--negative { color: var(--report-negative); }
	.acc_pct--neutral  { color: var(--report-text-muted); }
}

.theme_row_white { background: var(--report-bg); }

// Ancla del tooltip: la caja envuelve sólo el número, así el tooltip aparece
// sobre el texto (alineado a la derecha) y no centrado en un <td> ancho.
.cell_value {
	display: inline-block;
}

.clickable_cell {
	cursor: pointer;
	transition: background-color 0.12s ease;

	&:hover {
		background-color: var(--report-accent-bg);
	}
}

.row-level-2 {
	font-weight: 700;
	background: var(--report-row-emphasis);
}

.row-level-3 {
	color: var(--report-text-muted-strong);

	th, td {
		font-size: 12.5px !important;
		color: var(--report-text-muted) !important;
	}
}

.pl-5 { padding-left: 32px !important; }

.expand-icon {
	width: 14px;
	height: 14px;
	transition: transform 0.3s ease;
	color: var(--report-text-muted-soft);
}

.rotate-icon { transform: rotate(180deg); }
</style>
